'use client'
import { useGetFareCalendarQuery } from '@/src/api/rtkQuery/calendarApi'
import { IRange } from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/hooks/search-inputs-manager'
import useFormatCurrencyForSrp from '@/src/hooks/currency/useFormatCurrencyForSrp'
import { useGetSearchParamsObj } from '@/src/hooks/srp/searchParams'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { getFormattedDate } from '@/src/utils/date'
import { filterVisibleHolidays } from '@/src/utils/holidayCalendar/filterVisibleHolidays'
import { getHolidayConfig } from '@/src/utils/holidayCalendar/getHolidayConfig'
import { buildFareCalendarRequest } from '@/src/utils/srp/searchUtils/buildFareCalendarRequest'
import dayjs from 'dayjs'
import { useTranslations } from 'next-intl'
import { useParams } from 'next/navigation'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { DateSelector } from './dateSelector'
import { isFlyin } from '@/src/utils/generalUtils/isFlyin'

interface DateSelectorWrapperProps {
    open: boolean
    onOpen: () => void
    onClose: () => void
    isRoundTrip: boolean
    range: IRange
    onDateSelect: (dates: IRange) => void
    minDate?: Date | null
}

const DateSelectorWrapper: React.FC<DateSelectorWrapperProps> = ({
    open,
    onOpen,
    onClose,
    isRoundTrip,
    range,
    onDateSelect,
    minDate
}) => {
    // State management
    const [activeOnward, setActiveOnward] = useState(true)
    const [activeReturn, setActiveReturn] = useState(false)
    const [localRange, setLocalRange] = useState(range)
    const [shouldClearFareData, setShouldClearFareData] = useState(false)

    const t = useTranslations('common')
    const routeParams = useParams()
    const locale = (routeParams?.locale as string) || 'en'

    // Get search params and jsons for API call
    const { urlQueryObj } = useGetSearchParamsObj()
    const { jsons, isPerPaxPricingEnabled } = useSearchResults()
    const { formatCurrency } = useFormatCurrencyForSrp()

    // Memoize search parameters to prevent recalculation on every render
    const searchParams = useMemo(() => ({
        origin: urlQueryObj?.from || '',
        dest: urlQueryObj?.to || '',
        adult: Number(urlQueryObj?.adults) || 1,
        child: Number(urlQueryObj?.childs) || 0,
        infant: Number(urlQueryObj?.infants) || 0,
        cabin: urlQueryObj?.class?.toUpperCase() || 'ECONOMY',
        returnDate: urlQueryObj?.return_date || ''
    }), [urlQueryObj])

    // Memoize derived values
    const partnerId = useMemo(() => jsons?.userInfo?.partnerId || '', [jsons?.userInfo?.partnerId])
    const perPaxPricingRequired = useMemo(() => !searchParams.returnDate && isPerPaxPricingEnabled, [searchParams.returnDate, isPerPaxPricingEnabled])

    // Format date for display
    const formatDateDisplay = useCallback((date: Date | null) => {
        if (!date) return 'Select Date'
        return getFormattedDate(date, 'DD MMM YYYY')
    }, [])

    // Computed values

    const departureDate = useMemo(() => formatDateDisplay(localRange.from), [localRange.from, formatDateDisplay])

    const returnDate = useMemo(() => formatDateDisplay(localRange.to), [localRange.to, formatDateDisplay])

    const returnTabEnabled = useMemo(() => !!localRange.from, [localRange.from])

    // Get and filter visible holidays
    const allHolidays = useMemo(() => getHolidayConfig(locale), [locale])
    const visibleHolidays = useMemo(() => filterVisibleHolidays(allHolidays), [allHolidays])

    // Sync local range with prop changes
    useEffect(() => {
        setLocalRange(range)
    }, [range])

    // Reset state when opening
    useEffect(() => {
        if (open) {
            setActiveOnward(true)
            setActiveReturn(false)
        }
    }, [open])

    const handleSubmit = useCallback(() => {
        onDateSelect(localRange)
        onClose()
    }, [localRange, onDateSelect, onClose])
    // Handle date click from calendar
    const handleDateClick = useCallback(
        (dates: any) => {
            setLocalRange(dates)
            if (isRoundTrip && dates['to'] && activeReturn) {
                // Both dates selected for round trip - apply and close
                onDateSelect(dates)
            } else if (isRoundTrip && dates['from'] && activeOnward) {
                // Only departure date selected for round trip - switch to return
                setActiveOnward(false)
                setActiveReturn(true)
                // Keep calendar open for return date selection
            } else if (!isRoundTrip) {
                // One way trip completed - apply and close
                onDateSelect(dates)
                onClose()
            }
        },
        [isRoundTrip, activeReturn, activeOnward, onDateSelect, onClose]
    )

    // Handle apply button
    const handleApply = useCallback(() => {
        onDateSelect(localRange)
        onClose()
    }, [localRange, onDateSelect, onClose])

    // Handle departure tab click
    const handleDepartureClick = useCallback(() => {
        setActiveOnward(true)
        setActiveReturn(false)
        setShouldClearFareData(false) // Restore fare data when switching back to departure
    }, [])

    // Handle return tab click
    const handleReturnClick = useCallback(() => {
        if (localRange.from) {
            setActiveOnward(false)
            setActiveReturn(true)
            setShouldClearFareData(true) // Clear fare data when switching to return
        }
    }, [localRange.from])

    // Calculate date range and payload from monthRange string
    const calculateFareCalendarPayload = useCallback((monthRange: string) => {
        if (!monthRange || !searchParams.origin || !searchParams.dest) return null

        // Parse monthRange: "DD-MM-YYYY_DD-MM-YYYY"
        const [startMonthStr, endMonthStr] = monthRange.split('_')
        if (!startMonthStr || !endMonthStr) return null

        const startMonth = dayjs(startMonthStr, 'DD-MM-YYYY')
        const endMonth = dayjs(endMonthStr, 'DD-MM-YYYY')
        const today = dayjs().startOf('day')

        // Calculate startDate: today or first of month, whichever is later
        const startDate = startMonth.isBefore(today) ? today : startMonth.startOf('month')

        // Calculate daysCount: difference between startDate and end of endMonth
        const daysCount = endMonth.endOf('month').diff(startDate, 'day') + 1

        if (daysCount <= 0) return null

        return buildFareCalendarRequest({
            startDate: startDate.format('DD/MM/YYYY'),
            daysCount,
            searchParams: urlQueryObj,
            partnerId: isFlyin() ? partnerId : undefined
        })
    }, [searchParams, partnerId])

    // State for current API payload
    const [currentPayload, setCurrentPayload] = useState<ReturnType<typeof calculateFareCalendarPayload>>(null)

    // Make API call when payload is available
    // skip: Only make API call for one-way trips (not round trip) when we have payload and required search params
    const {
        data: fareCalendarData,
        error: fareCalendarError,
        isError: isFareCalendarError
    } = useGetFareCalendarQuery(
        {
            payload: currentPayload!,
            perPaxPricingRequired
        },
        {
            skip: isRoundTrip || !currentPayload || !searchParams.origin || !searchParams.dest
        }
    )

    // Use derived values from RTK Query data
    const fareCalendarFromApi = useMemo(() => {
        if (isFareCalendarError) {
            console.warn('Fare calendar API error:', fareCalendarError)
            return { fareCalendar: {}, cheapestPricesByMonth: {} }
        }
        return {
            fareCalendar: fareCalendarData?.fareCalendar || {},
            cheapestPricesByMonth: fareCalendarData?.cheapestPricesByMonth || {}
        }
    }, [fareCalendarData, isFareCalendarError, fareCalendarError])

    // Final fare calendar data: use API data unless we need to clear it
    const fareCalendar = useMemo(() => {
        return shouldClearFareData ? {} : fareCalendarFromApi.fareCalendar
    }, [shouldClearFareData, fareCalendarFromApi.fareCalendar])

    const cheapestPricesByMonth = useMemo(() => {
        return shouldClearFareData ? {} : fareCalendarFromApi.cheapestPricesByMonth
    }, [shouldClearFareData, fareCalendarFromApi.cheapestPricesByMonth])

    // Reset states when calendar closes
    useEffect(() => {
        if (!open) {
            setShouldClearFareData(false)
            setCurrentPayload(null)
        }
    }, [open])

    // Initial API call when calendar opens (only for one-way trips)
    useEffect(() => {
        if (!isRoundTrip && open && searchParams.origin && searchParams.dest && !currentPayload) {
            // Calculate initial month range for 13 months (mobile shows 13 months)
            const today = dayjs()
            const startMonth = today.startOf('month').format('DD-MM-YYYY')
            const endMonth = today.add(12, 'month').endOf('month').format('DD-MM-YYYY')
            const initialMonthRange = `${startMonth}_${endMonth}`

            const payload = calculateFareCalendarPayload(initialMonthRange)
            if (payload) {
                setCurrentPayload(payload)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRoundTrip, open, searchParams.origin, searchParams.dest, calculateFareCalendarPayload])

    // Handle fare calendar direction click (only for one-way trips)
    // Note: Mobile calendar shows all 13 months at once, so initial API call covers all months
    // This is kept for consistency with desktop API, but not actively used in mobile
    const directionClick = useCallback((departRange: string) => {
        // Only trigger API call for one-way trips
        if (!isRoundTrip) {
            // Calculate payload from monthRange and trigger API call
            const payload = calculateFareCalendarPayload(departRange)
            if (payload) {
                setCurrentPayload(payload)
            }
        }
    }, [calculateFareCalendarPayload, isRoundTrip])

    return (
        <DateSelector
            isRtl={locale === 'ar'}
            open={open}
            onOpen={onOpen}
            onClose={onClose}
            isRoundTrip={isRoundTrip}
            departureDate={departureDate}
            returnDate={returnDate}
            activeOnward={activeOnward}
            activeReturn={activeReturn}
            localRange={localRange}
            returnTabEnabled={returnTabEnabled}
            onDepartureClick={handleDepartureClick}
            onReturnClick={handleReturnClick}
            onSubmit={handleSubmit}
            onDateClick={handleDateClick}
            onApply={handleApply}
            directionClick={directionClick}
            t={t}
            minDate={minDate}
            fareCalendar={fareCalendar}
            cheapestPricesByMonth={cheapestPricesByMonth}
            formatCurrency={formatCurrency}
            holidays={visibleHolidays}
        />
    )
}

export { DateSelectorWrapper as DateSelector }
export default DateSelectorWrapper
